import React from 'react'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Progress,
  Spinner,
} from '@heroui/react'

import {
  type BatchConversionResult,
  type BatchLoopRepairResult,
} from '../services/mediaManagementService'

// Simple icon components
const ErrorIcon = () => <span>❌</span>

interface ConversionProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchConversionResult | null
  error: string | null
}

interface LoopRepairProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchLoopRepairResult | null
  error: string | null
}

interface ProgressModalProps {
  isOpen: boolean
  onClose: () => void
  type: 'conversion' | 'repair' | null
  conversionProgress?: ConversionProgress
  loopRepairProgress?: LoopRepairProgress
  conversionTitle?: string
}

export const ProgressModal: React.FC<ProgressModalProps> = ({
  isOpen,
  onClose,
  type,
  conversionProgress,
  loopRepairProgress,
  conversionTitle,
}) => {
  const isRunning =
    (type === 'conversion' && conversionProgress?.isRunning) ||
    (type === 'repair' && loopRepairProgress?.isRunning)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isDismissable={false}
      hideCloseButton={isRunning}
      size="lg"
    >
      <ModalContent>
        <ModalHeader>
          {type === 'conversion'
            ? conversionTitle || 'Converting GIFs to MP4'
            : 'Repairing Loop Extensions'}
        </ModalHeader>
        <ModalBody>
          {type === 'conversion' && conversionProgress && (
            <div className="space-y-4">
              {conversionProgress.isRunning && (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Spinner size="sm" className="mr-2" />
                    <span>
                      Processing {conversionProgress.currentBatch.length}{' '}
                      GIFs...
                    </span>
                  </div>
                  <Progress isIndeterminate className="w-full" />
                </div>
              )}

              {conversionProgress.results && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {conversionProgress.results.totalRequested}
                      </div>
                      <div className="text-sm text-slate-600">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {conversionProgress.results.successful}
                      </div>
                      <div className="text-sm text-slate-600">Successful</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {conversionProgress.results.failed}
                      </div>
                      <div className="text-sm text-slate-600">Failed</div>
                    </div>
                  </div>

                  {conversionProgress.results.results.some(
                    (r) => !r.success
                  ) && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Failed Conversions:</h4>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {conversionProgress.results.results
                          .filter((r) => !r.success)
                          .map((result) => (
                            <div
                              key={result.gifId}
                              className="flex items-center text-sm"
                            >
                              <ErrorIcon />
                              <span className="font-mono ml-2">
                                {result.gifId}
                              </span>
                              <span className="ml-2 text-slate-600">
                                - {result.error}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {conversionProgress.error && (
                <div className="text-red-600 text-sm">
                  Error: {conversionProgress.error}
                </div>
              )}
            </div>
          )}

          {type === 'repair' && loopRepairProgress && (
            <div className="space-y-4">
              {loopRepairProgress.isRunning && (
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Spinner size="sm" className="mr-2" />
                    <span>
                      Processing {loopRepairProgress.currentBatch.length}{' '}
                      GIFs...
                    </span>
                  </div>
                  <Progress isIndeterminate className="w-full" />
                </div>
              )}

              {loopRepairProgress.results && (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold">
                        {loopRepairProgress.results.totalRequested}
                      </div>
                      <div className="text-sm text-slate-600">Total</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">
                        {loopRepairProgress.results.successful}
                      </div>
                      <div className="text-sm text-slate-600">Successful</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-600">
                        {loopRepairProgress.results.failed}
                      </div>
                      <div className="text-sm text-slate-600">Failed</div>
                    </div>
                  </div>

                  {loopRepairProgress.results.results.some(
                    (r) => !r.success
                  ) && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Failed Repairs:</h4>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {loopRepairProgress.results.results
                          .filter((r) => !r.success)
                          .map((result) => (
                            <div
                              key={result.gifId}
                              className="flex items-center text-sm"
                            >
                              <ErrorIcon />
                              <span className="font-mono ml-2">
                                {result.gifId}
                              </span>
                              <span className="ml-2 text-slate-600">
                                - {result.error}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {loopRepairProgress.error && (
                <div className="text-red-600 text-sm">
                  Error: {loopRepairProgress.error}
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onPress={onClose} isDisabled={isRunning}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default ProgressModal
